// w01 Sevinch — editorial heirloom wedding preview card.
//
// The catalog card uses the operator-approved "outer frame + inner card"
// structure rendered in a heirloom-stationery idiom: a warm cream→terracotta
// double-band gradient frame surrounds the paper-white inner card. A round
// sepia-toned photo medallion sits at the centre with a wax-seal monogram
// dot at the top; names in Cormorant Garamond italic with a draw-in rule
// beneath. The eyebrow + footer pick up the masthead language of the
// original ("TAKLIFNOMA · VOL. I").
import { Photo, type LayoutCtx } from "./_shared";

// Cream → terracotta heirloom band — alternating warm stops.
const HEIRLOOM_GRADIENT =
  "linear-gradient(135deg, #F1EBE0 0%, #D9B9A6 22%, #F1EBE0 45%, #B98B79 65%, #F1EBE0 100%)";

export function PhotoBleedLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  const parts = names.split("&").map((s) => s.trim());
  const first = parts[0] || names;
  const second = parts[1] || "";
  const captionDate = formatDate(date);
  const monogram = `${(first[0] || "A").toUpperCase()}·${(second[0] || "S").toUpperCase()}`;

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: HEIRLOOM_GRADIENT,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        {/* Masthead eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TAKLIFNOMA · VOL. I
        </div>

        {/* Photo medallion */}
        <div
          style={{
            width: "60%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          {/* Wax-seal monogram on top */}
          <div
            style={{
              position: "absolute",
              top: -10,
              left: "50%",
              transform: "translateX(-50%)",
              width: 26,
              height: 26,
              borderRadius: "50%",
              background: `radial-gradient(circle at 35% 30%, ${accent}ee, ${accent}bb 60%, ${accent}88 100%)`,
              boxShadow: `0 1px 2px ${ink}44, inset 0 0 4px ${ink}33`,
              display: "grid",
              placeItems: "center",
              color: bg,
              fontFamily: font,
              fontStyle: "italic",
              fontSize: 8,
              fontWeight: 600,
              letterSpacing: "-0.02em",
              zIndex: 2,
            }}
            aria-hidden
          >
            {monogram}
          </div>

          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: HEIRLOOM_GRADIENT,
              padding: "5%",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                padding: "4%",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Photo
                  src={photo}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  fallback={
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: `radial-gradient(circle at 50% 45%, ${accent}44, ${bg})`,
                      }}
                    />
                  }
                  sepia
                />
              </div>
            </div>
          </div>
        </div>

        {/* Names */}
        <div style={{ fontFamily: font, fontStyle: "italic", fontSize: 28, lineHeight: 1, color: ink }}>
          {first}
        </div>
        {second && (
          <>
            <div
              style={{
                fontFamily: font,
                fontStyle: "italic",
                fontSize: 18,
                color: accent,
                lineHeight: 1.4,
              }}
            >
              &
            </div>
            <div style={{ fontFamily: font, fontStyle: "italic", fontSize: 28, lineHeight: 1, color: ink }}>
              {second}
            </div>
          </>
        )}

        {/* Hairline rule */}
        <div
          style={{
            height: 1,
            width: "32%",
            background: `linear-gradient(to right, transparent, ${accent} 30%, ${accent} 70%, transparent)`,
            margin: "10px auto 0",
            opacity: 0.85,
          }}
        />

        <div style={{ flex: 1 }} />

        {/* Caption */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          SAVE THE DATE · {captionDate}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

function formatDate(d: string): string {
  const m = d.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return d.toUpperCase();
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const year = m[3];
  const months = [
    "YANVAR", "FEVRAL", "MART", "APREL", "MAY", "IYUN",
    "IYUL", "AVGUST", "SENTABR", "OKTABR", "NOYABR", "DEKABR",
  ];
  return `${day} ${months[month - 1] ?? ""} ${year}`;
}
